import React, { useState, useRef, useEffect } from 'react';
import './OptimizedImage.css';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  sizes?: string;
  quality?: number;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className = '',
  loading = 'lazy',
  sizes,
  quality = 80
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(loading === 'eager');
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (loading === 'eager') return;

    // Add a small delay to ensure the component is mounted
    const timer = setTimeout(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsInView(true);
              observer.disconnect();
            }
          });
        },
        {
          rootMargin: '100px', // Increased margin for better detection
          threshold: 0.1 // Trigger when 10% of element is visible
        }
      );

      if (imgRef.current) {
        observer.observe(imgRef.current);
      } else {
        // If ref is not available, show image anyway
        setIsInView(true);
      }

      return () => observer.disconnect();
    }, 100);

    return () => clearTimeout(timer);
  }, [loading]);

  // Generate WebP and fallback paths
  const getOptimizedSrc = (originalSrc: string) => {
    // Convert /assets/images/folder/image.png to /assets/images/webp/folder/image.webp
    const webpSrc = originalSrc.replace('/assets/images/', '/assets/images/webp/').replace('.png', '.webp');
    return webpSrc;
  };

  const webpSrc = getOptimizedSrc(src);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    console.log(`Image failed to load: ${imageSrc}, falling back to PNG`);
    setHasError(true);
  };

  // Support for WebP with fallback - more reliable detection
  const supportsWebP = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      const dataURL = canvas.toDataURL('image/webp');
      return dataURL.indexOf('data:image/webp') === 0;
    } catch (e) {
      return false;
    }
  };

  // Use WebP if supported and no error, otherwise use original PNG
  const imageSrc = !hasError && supportsWebP() ? webpSrc : src;

  return (
    <div className={`optimized-image-container ${className}`} ref={imgRef}>
      {isInView && (
        <>
          {/* Placeholder while loading */}
          {!isLoaded && (
            <div className="optimized-image-placeholder">
              <div className="optimized-image-skeleton"></div>
            </div>
          )}

          {/* Actual image */}
          <img
            src={imageSrc}
            alt={alt}
            className={`optimized-image ${isLoaded ? 'loaded' : 'loading'}`}
            loading={loading}
            sizes={sizes}
            onLoad={handleLoad}
            onError={handleError}
            style={{
              opacity: isLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out'
            }}
          />
        </>
      )}
    </div>
  );
};

export default OptimizedImage;